import { useEffect, useMemo, useState } from "react";
import { adminSearchMeasurements } from "../../api/admin";

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
};

const formatNumber = (value, unit = "") => {
  const num = Number(value || 0);

  if (!Number.isFinite(num)) return `0${unit}`;

  return `${num.toLocaleString("ko-KR", {
    maximumFractionDigits: 1,
  })}${unit}`;
};

const formatDuration = (seconds) => {
  const total = Number(seconds || 0);

  if (!Number.isFinite(total) || total <= 0) return "-";

  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = Math.floor(total % 60);

  if (h > 0) return `${h}시간 ${m}분`;
  if (m > 0) return `${m}분 ${s}초`;
  return `${s}초`;
};

const getWorkoutTypeLabel = (type) => {
  switch (type) {
    case "strength":
      return "근력";
    case "cardio":
      return "유산소";
    default:
      return type || "-";
  }
};

export default function Measurements() {
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [skip, setSkip] = useState(0);
  const [limit] = useState(30);
  const [total, setTotal] = useState(0);

  const currentPage = Math.floor(skip / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const summary = useMemo(() => {
    return rows.reduce(
      (acc, item) => {
        acc.count += 1;
        acc.duration += Number(item.duration_sec || 0);
        acc.calories += Number(item.calories || 0);
        acc.volume += Number(item.total_volume || 0);
        acc.distance += Number(item.distance_m || 0);
        return acc;
      },
      {
        count: 0,
        duration: 0,
        calories: 0,
        volume: 0,
        distance: 0,
      }
    );
  }, [rows]);

  const fetchMeasurements = async ({ nextSkip = skip } = {}) => {
    try {
      setLoading(true);
      setError("");

      const res = await adminSearchMeasurements({
        q,
        start,
        end,
        skip: nextSkip,
        limit,
      });

      const items = Array.isArray(res?.items) ? res.items : [];

      setRows(items);
      setTotal(Number(res?.total || 0));
      setSkip(Number(res?.skip || nextSkip || 0));

      if (items.length > 0) {
        setSelected((prev) => {
          if (!prev) return items[0];
          return items.find((item) => item.id === prev.id) || items[0];
        });
      } else {
        setSelected(null);
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || "측정데이터를 불러오지 못했습니다.");
      setRows([]);
      setSelected(null);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeasurements({ nextSkip: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMeasurements({ nextSkip: 0 });
  };

  const handleReset = () => {
    setQ("");
    setStart("");
    setEnd("");
    setTimeout(() => {
      fetchMeasurements({ nextSkip: 0 });
    }, 0);
  };

  const handlePrev = () => {
    if (skip <= 0 || loading) return;
    fetchMeasurements({ nextSkip: Math.max(0, skip - limit) });
  };

  const handleNext = () => {
    if (skip + limit >= total || loading) return;
    fetchMeasurements({ nextSkip: skip + limit });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-white p-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-bold text-gray-900">측정데이터</h2>
          <p className="text-sm text-gray-500">
            슈퍼관리자는 전체 사업장 데이터를 볼 수 있고, 일반관리자는 본인
            사업장의 사용자 운동데이터만 조회됩니다.
          </p>
        </div>

        <form
          onSubmit={handleSearch}
          className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[1fr_160px_160px_auto_auto]"
        >
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="이름, 전화번호, 사업장, 운동명 검색"
            className="rounded-lg border px-3 py-2 text-sm outline-none focus:border-blue-500"
          />

          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm outline-none focus:border-blue-500"
          />

          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm outline-none focus:border-blue-500"
          />

          <button
            type="submit"
            disabled={loading}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {loading ? "조회중..." : "조회"}
          </button>

          <button
            type="button"
            onClick={handleReset}
            disabled={loading}
            className="rounded-lg border px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
          >
            초기화
          </button>
        </form>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-gray-500">현재 페이지 기록</div>
          <div className="mt-1 text-2xl font-bold">{summary.count}</div>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-gray-500">총 운동시간</div>
          <div className="mt-1 text-2xl font-bold">
            {formatDuration(summary.duration)}
          </div>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-gray-500">총 칼로리</div>
          <div className="mt-1 text-2xl font-bold">
            {formatNumber(summary.calories, " kcal")}
          </div>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-gray-500">총 볼륨</div>
          <div className="mt-1 text-2xl font-bold">
            {formatNumber(summary.volume, " kg")}
          </div>
        </div>

        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-gray-500">총 거리</div>
          <div className="mt-1 text-2xl font-bold">
            {formatNumber(summary.distance / 1000, " km")}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_420px]">
        <div className="overflow-hidden rounded-xl border bg-white">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <h3 className="font-bold text-gray-900">운동데이터 목록</h3>
              <p className="text-xs text-gray-500">
                총 {total.toLocaleString("ko-KR")}건
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <button
                type="button"
                onClick={handlePrev}
                disabled={skip <= 0 || loading}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
              >
                이전
              </button>

              <span className="text-gray-500">
                {currentPage} / {totalPages}
              </span>

              <button
                type="button"
                onClick={handleNext}
                disabled={skip + limit >= total || loading}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40"
              >
                다음
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 text-center">
                    날짜
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-center">
                    회원
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-center">
                    사업장
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-center">
                    구분
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-center">
                    운동명
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-center">
                    시간
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-center">
                    칼로리
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-center">
                    볼륨/거리
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {loading && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-gray-500"
                    >
                      측정데이터를 불러오는 중입니다.
                    </td>
                  </tr>
                )}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-gray-500"
                    >
                      조회된 측정데이터가 없습니다.
                    </td>
                  </tr>
                )}

                {!loading &&
                  rows.map((item) => {
                    const active = selected?.id === item.id;

                    return (
                      <tr
                        key={item.id}
                        onClick={() => setSelected(item)}
                        className={`cursor-pointer hover:bg-gray-50 ${active ? "bg-blue-50" : "bg-white"
                          }`}
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-center">
                          {formatDateTime(item.started_at)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-center">
                          <div className="font-semibold text-gray-900">
                            {item.user_name || "-"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.user_phone || "-"}
                          </div>
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-center">
                          {item.workplace || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-center">
                          <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">
                            {getWorkoutTypeLabel(item.workout_type)}
                          </span>
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-center">
                          {item.exercise_name || item.category || "-"}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-center">
                          {formatDuration(item.duration_sec)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-center">
                          {formatNumber(item.calories, " kcal")}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-center">
                          {item.workout_type === "cardio"
                            ? formatNumber(Number(item.distance_m || 0) / 1000, " km")
                            : formatNumber(item.total_volume, " kg")}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border bg-white">
          <div className="border-b px-4 py-3">
            <h3 className="font-bold text-gray-900">상세 데이터</h3>
            <p className="text-xs text-gray-500">
              목록에서 기록을 선택하면 세부 데이터가 표시됩니다.
            </p>
          </div>

          {!selected ? (
            <div className="p-6 text-sm text-gray-500">
              선택된 운동데이터가 없습니다.
            </div>
          ) : (
            <div className="space-y-4 p-4">
              <div>
                <div className="text-xs text-gray-500">회원</div>
                <div className="mt-1 text-lg font-bold text-gray-900">
                  {selected.user_name || "-"}
                </div>
                <div className="text-sm text-gray-500">
                  {selected.user_phone || "-"}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <InfoBox label="사업장" value={selected.workplace || "-"} />
                <InfoBox
                  label="운동구분"
                  value={getWorkoutTypeLabel(selected.workout_type)}
                />
                <InfoBox
                  label="운동명"
                  value={selected.exercise_name || selected.category || "-"}
                />
                <InfoBox
                  label="운동일시"
                  value={formatDateTime(selected.started_at)}
                />
                <InfoBox
                  label="운동시간"
                  value={formatDuration(selected.duration_sec)}
                />
                <InfoBox
                  label="칼로리"
                  value={formatNumber(selected.calories, " kcal")}
                />
              </div>

              {selected.workout_type === "strength" && (
                <StrengthDetail selected={selected} />
              )}

              {selected.workout_type === "cardio" && (
                <CardioDetail selected={selected} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="rounded-lg bg-gray-50 p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1 font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function StrengthDetail({ selected }) {
  const sets = Array.isArray(selected.strength_sets)
    ? selected.strength_sets
    : [];

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="font-bold text-gray-900">근력 세트 기록</h4>
        <span className="text-xs text-gray-500">
          총 {sets.length.toLocaleString("ko-KR")}세트
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <InfoBox
          label="총 볼륨"
          value={formatNumber(selected.total_volume, " kg")}
        />
        <InfoBox
          label="최고 중량"
          value={formatNumber(selected.max_weight, " kg")}
        />
      </div>

      <div className="mt-3 overflow-hidden rounded-lg border">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">세트</th>
              <th className="px-3 py-2 text-right">무게</th>
              <th className="px-3 py-2 text-right">횟수</th>
              <th className="px-3 py-2 text-right">휴식</th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {sets.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                  세트 기록이 없습니다.
                </td>
              </tr>
            )}

            {sets.map((set, index) => (
              <tr key={`${set.id || index}`}>
                <td className="px-3 py-2">{index + 1}</td>
                <td className="px-3 py-2 text-right">
                  {formatNumber(set.weight, " kg")}
                </td>
                <td className="px-3 py-2 text-right">
                  {formatNumber(set.reps, " 회")}
                </td>
                <td className="px-3 py-2 text-right">
                  {formatDuration(set.rest_sec)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CardioDetail({ selected }) {
  const cardio = selected.cardio || {};

  return (
    <div>
      <h4 className="mb-2 font-bold text-gray-900">유산소 상세 기록</h4>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <InfoBox
          label="거리"
          value={formatNumber(Number(cardio.distance_m || 0) / 1000, " km")}
        />
        <InfoBox
          label="평균 속도"
          value={formatNumber(cardio.avg_speed, " km/h")}
        />
        <InfoBox label="경사" value={formatNumber(cardio.incline, " %")} />
        <InfoBox label="레벨" value={formatNumber(cardio.level, "")} />
        <InfoBox label="저항" value={formatNumber(cardio.resistance, "")} />
        <InfoBox label="와트" value={formatNumber(cardio.watt, " W")} />
      </div>
    </div>
  );
}